import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productionOrdersService, productsService, warehousesService, suppliesService, resourcesService, movementTypesService } from '../../services/productionService';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { MaskedInput } from '../../components/ui/masked-input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Autocomplete } from '../../components/ui/autocomplete';
import TableComponent from '../../components/TableComponent';
import Drawer from '../../components/Drawer';
import ConfirmDialog from '../../components/ConfirmDialog';
import ProductionReportForm from '../../components/production/ProductionReportForm';
import BOMEditor from '../../components/production/BOMEditor';
import { useToast } from '../../contexts/ToastContext';
import usePageTitle from '../../utils/usePageTitle';
import { formatBRL, formatDateBR, formatDuration, formatQuantity, localDateISO, parseQuantityInput } from '../../lib/utils';

function ProductionOrderDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id || id === 'new';
    const { toast, toastApiError } = useToast();
    
    const [order, setOrder] = useState(null);
    const [products, setProducts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [reports, setReports] = useState([]);
    const [bomData, setBomData] = useState(null);
    const [bomSuppliesCatalog, setBomSuppliesCatalog] = useState([]);
    const [bomResourcesCatalog, setBomResourcesCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);
    const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportFormKey, setReportFormKey] = useState(0);
    const [movementTypes, setMovementTypes] = useState([]);
    const [defaultProdMovementTypeId, setDefaultProdMovementTypeId] = useState(null);

    const [cancelDialog, setCancelDialog] = useState({ open: false });
    const [finishDialog, setFinishDialog] = useState({ open: false });
    const [activeTab, setActiveTab] = useState('general');
    const [bomDrawerOpen, setBomDrawerOpen] = useState(false);
    const [bomDraft, setBomDraft] = useState(null);
    const [bomDrawerKey, setBomDrawerKey] = useState(0);
    const [bomSaving, setBomSaving] = useState(false);
    const bomEditorRef = useRef(null);

    const [formData, setFormData] = useState({
        product_id: '',
        planned_quantity: '',
        target_warehouse_id: '',
        date: localDateISO(),
        notes: '',
    });

    useEffect(() => {
        if (isNew) {
            loadProducts();
            loadWarehouses();
            setLoading(false);
        } else if (id) {
            loadOrder();
        }
    }, [id, isNew]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await movementTypesService.list({ per_page: 500 });
                const rows = res.data?.data || res.data || [];
                const arr = Array.isArray(rows) ? rows : [];
                if (!cancelled) {
                    setMovementTypes(arr);
                    const pr = arr.find((t) => String(t.code || '').toUpperCase() === 'PR');
                    setDefaultProdMovementTypeId(pr?.id ?? null);
                }
            } catch {
                /* ignore - formulário continua editável sem default */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isNew && id) {
            loadProducts();
            loadWarehouses();
            loadReports();
            loadBomCatalog();
        }
    }, [id, isNew]);

    const applyOrderData = (data) => {
        if (!data) return;
        setOrder(data);
        setFormData({
            product_id: data.product_id || '',
            planned_quantity: data.planned_quantity || '',
            target_warehouse_id: data.target_warehouse_id || '',
            date: data.date || localDateISO(),
            notes: data.notes || '',
        });
        const bomSuppliesSrc = Array.isArray(data.bom_supplies)
            ? data.bom_supplies
            : data.product?.supplies || [];
        const bomResourcesSrc = Array.isArray(data.bom_resources)
            ? data.bom_resources
            : data.product?.resources || [];
        setBomData({
            supplies: bomSuppliesSrc.map((ps) => ({
                id: ps.id,
                supply_id: ps.supply_id,
                quantity_per_unit: ps.quantity_per_unit,
                supply: ps.supply,
            })),
            resources: bomResourcesSrc.map((pr) => ({
                id: pr.id,
                resource_id: pr.resource_id,
                quantity_per_unit: pr.quantity_per_unit,
                resource: pr.resource,
            })),
        });
    };

    const loadProducts = async () => {
        try {
            const response = await productsService.list({ per_page: 1000 });
            setProducts(response.data.data || response.data);
        } catch (error) {
            console.error('Error loading products:', error);
        }
    };

    const loadWarehouses = async () => {
        try {
            const response = await warehousesService.list();
            setWarehouses(response.data.data || response.data);
        } catch (error) {
            console.error('Error loading warehouses:', error);
        }
    };

    const loadOrder = async () => {
        setLoading(true);
        try {
            const response = await productionOrdersService.get(id);
            const data = response.data.data || response.data;
            applyOrderData(data);
        } catch (error) {
            console.error('Error loading order:', error);
            toastApiError(error, { title: 'Erro ao carregar', fallback: 'Erro ao carregar ordem de produção' });
            navigate('/production/orders');
        } finally {
            setLoading(false);
        }
    };

    const loadReports = async () => {
        try {
            const response = await productionOrdersService.getReports(id);
            const reportsData = response.data.data || response.data;
            setReports(reportsData);
        } catch (error) {
            console.error('Error loading reports:', error);
        }
    };

    const loadBomCatalog = async () => {
        try {
            const [suppliesResponse, resourcesResponse] = await Promise.all([
                suppliesService.list({ per_page: 1000 }),
                resourcesService.list({ per_page: 1000 }),
            ]);

            setBomSuppliesCatalog(suppliesResponse.data.data || suppliesResponse.data);
            setBomResourcesCatalog(resourcesResponse.data.data || resourcesResponse.data);
        } catch (error) {
            console.error('Error loading BOM catalogs:', error);
        }
    };

    const cloneBomData = (bom) => ({
        supplies: (bom?.supplies || []).map((s) => ({ ...s })),
        resources: (bom?.resources || []).map((r) => ({ ...r })),
    });

    const openBomDrawer = () => {
        setBomDraft(cloneBomData(bomData));
        setBomDrawerKey((k) => k + 1);
        setBomDrawerOpen(true);
    };

    const closeBomDrawer = () => {
        setBomDrawerOpen(false);
        setBomDraft(null);
    };

    const handleBomSave = async (payload) => {
        setBomSaving(true);
        try {
            const response = await productionOrdersService.updateBom(id, payload);
            const data = response.data.data || response.data;
            applyOrderData(data);
            closeBomDrawer();
            toast({ title: 'BOM atualizado', variant: 'success' });
        } catch (error) {
            console.error('Error saving BOM:', error);
            toastApiError(error, { title: 'Erro ao salvar BOM', fallback: 'Erro ao salvar BOM' });
            throw error;
        } finally {
            setBomSaving(false);
        }
    };

    const handleReportSubmit = async (data) => {
        setReportLoading(true);
        try {
            const response = await productionOrdersService.report(id, data);
            const body = response.data.data || response.data;
            if (body.order) {
                applyOrderData(body.order);
            }
            if (body.report) {
                setReports((prev) => [body.report, ...(Array.isArray(prev) ? prev : [])]);
            }
            toast({ title: 'Produção registrada', variant: 'success' });
            setReportDrawerOpen(false);
            setReportFormKey((k) => k + 1);
        } catch (error) {
            console.error('Error reporting production:', error);
            toastApiError(error, { title: 'Erro ao registrar produção', fallback: 'Erro ao registrar produção' });
        } finally {
            setReportLoading(false);
        }
    };

    const handleCancel = async () => {
        try {
            const response = await productionOrdersService.cancel(id);
            const data = response.data.data || response.data;
            applyOrderData(data);
            setCancelDialog({ open: false });
            toast({ title: 'Ordem cancelada', variant: 'success' });
        } catch (error) {
            console.error('Error cancelling order:', error);
            toastApiError(error, { title: 'Erro ao cancelar', fallback: 'Erro ao cancelar ordem' });
        }
    };

    const handleFinish = async () => {
        try {
            const response = await productionOrdersService.finish(id);
            const data = response.data.data || response.data;
            applyOrderData(data);
            setFinishDialog({ open: false });
            toast({ title: 'Ordem finalizada', variant: 'success' });
        } catch (error) {
            console.error('Error finishing order:', error);
            toastApiError(error, { title: 'Erro ao finalizar', fallback: 'Erro ao finalizar ordem' });
        }
    };

    const handleFormChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleProductSelect = (value, option) => {
        const defaultWarehouseId = option?.default_warehouse_id;
        setFormData((prev) => ({
            ...prev,
            product_id: value ? value.toString() : '',
            target_warehouse_id:
                defaultWarehouseId != null && defaultWarehouseId !== ''
                    ? String(defaultWarehouseId)
                    : prev.target_warehouse_id,
        }));
    };

    const handleSave = async () => {
        setFormLoading(true);

        try {
            const payload = {
                ...formData,
                planned_quantity: parseQuantityInput(formData.planned_quantity),
                target_warehouse_id: parseInt(formData.target_warehouse_id),
            };

            if (isNew) {
                const response = await productionOrdersService.create(payload);
                const savedOrder = response.data.data || response.data;
                if (savedOrder.id) {
                    toast({ title: 'Ordem criada', variant: 'success' });
                    navigate(`/production/orders/${savedOrder.id}`);
                    return;
                }
            } else {
                const response = await productionOrdersService.update(id, payload);
                const data = response.data.data || response.data;
                applyOrderData(data);
                toast({ title: 'Ordem atualizada', variant: 'success' });
                return;
            }
        } catch (error) {
            console.error('Error saving order:', error);
            toastApiError(error, { title: 'Erro ao salvar', fallback: 'Erro ao salvar ordem de produção' });
        } finally {
            setFormLoading(false);
        }
    };

    const canEdit = order && order.status !== 'finished' && order.status !== 'cancelled';
    const readOnly = !isNew && !!order && !canEdit;

    usePageTitle(isNew ? 'Nova Ordem de Produção' : 'Ordem de Produção');

    const getStatusBadge = (status) => {
        const statusMap = {
            open: { label: 'Aberta', variant: 'default' },
            in_progress: { label: 'Em Produção', variant: 'warning' },
            finished: { label: 'Finalizada', variant: 'success' },
            cancelled: { label: 'Cancelada', variant: 'destructive' },
        };
        const config = statusMap[status] || { label: status, variant: 'default' };
        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    if (!isNew && !order && !loading) {
        return (
            <AppLayout>
                <div className="text-center py-12">
                    <p className="text-gray-500">Ordem de produção não encontrada</p>
                    <Button variant="outline" onClick={() => navigate('/production/orders')} className="mt-4">
                        Voltar
                    </Button>
                </div>
            </AppLayout>
        );
    }

    const reportColumns = [
        {
            key: 'datetime',
            label: 'Data',
            render: (value) => formatDateBR(value),
        },
        {
            key: 'duration_minutes',
            label: 'Duração',
            render: (value) => formatDuration(value),
        },
        {
            key: 'produced_quantity',
            label: 'Quantidade Produzida',
            render: (value) => formatQuantity(value),
        },
        {
            key: 'unit_cost',
            label: 'Custo Unitário',
            align: 'right',
            className: 'text-right tabular-nums',
            render: (value) => (value === null || value === undefined || value === '') ? '-' : formatBRL(value),
        },
        {
            key: 'supply_cost',
            label: 'Custo de Insumos',
            align: 'right',
            className: 'text-right tabular-nums',
            render: (value) => (value === null || value === undefined || value === '') ? '-' : formatBRL(value),
        },
        {
            key: 'resource_cost',
            label: 'Custo de Recursos',
            align: 'right',
            className: 'text-right tabular-nums',
            render: (value) => (value === null || value === undefined || value === '') ? '-' : formatBRL(value),
        },
        {
            key: 'total_cost',
            label: 'Custo Total',
            align: 'right',
            className: 'text-right tabular-nums',
            render: (value) => (value === null || value === undefined || value === '') ? '-' : formatBRL(value),
        },
        {
            key: 'notes',
            label: 'Observações',
            render: (value) => value || '-',
        },
    ];

    return (
        <AppLayout>
            <div className="space-y-6">
                {/* Header */}
                <PageHeader
                    title={isNew ? 'Nova Ordem de Produção' : `Ordem de Produção${order ? ` #${order.id}` : ''}`}
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Produção', path: '/production/orders' },
                        { label: 'Ordens de Produção', path: '/production/orders' },
                    ]}
                    additionalActions={
                        !isNew && order && order.status !== 'finished' && order.status !== 'cancelled'
                            ? [
                                {
                                    label: 'Cancelar',
                                    icon: 'cancel',
                                    variant: 'outline',
                                    onClick: () => setCancelDialog({ open: true }),
                                },
                                {
                                    label: 'Finalizar',
                                    icon: 'finish',
                                    variant: 'outline',
                                    onClick: () => setFinishDialog({ open: true }),
                                },
                            ]
                            : []
                    }
                    actions={[
                        {
                            label: 'Voltar',
                            icon: 'back',
                            variant: 'outline',
                            as: Link,
                            to: '/production/orders',
                        },
                        ...(isNew || canEdit
                            ? [
                                {
                                    label: formLoading ? 'Salvando...' : 'Salvar',
                                    icon: 'save',
                                    onClick: handleSave,
                                    disabled: formLoading || loading,
                                },
                            ]
                            : []),
                    ]}
                />

                {/* Status and Info Cards - sempre visíveis, com fallback em modo novo */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Card Status */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Status</p>
                                <p className="text-2xl font-bold mt-1 text-gray-900">
                                    {!order ? 'Aberta' :
                                     order.status === 'open' ? 'Aberta' :
                                     order.status === 'in_progress' ? 'Em Produção' :
                                     order.status === 'finished' ? 'Finalizada' :
                                     order.status === 'cancelled' ? 'Cancelada' : order.status}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Card Planejada */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Planejada</p>
                                <p className="text-2xl font-bold mt-1 text-gray-900">{formatQuantity(order ? order.planned_quantity : (formData.planned_quantity || 0))}</p>
                            </div>
                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Card Produzida */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Produzida</p>
                                <p className="text-2xl font-bold mt-1 text-gray-900">{formatQuantity(order ? order.produced_quantity : 0)}</p>
                            </div>
                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Card Progresso */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Progresso</p>
                                <p className="text-2xl font-bold mt-1 text-gray-900">
                                    {order && order.planned_quantity > 0
                                        ? (((order.produced_quantity || 0) / order.planned_quantity) * 100).toFixed(2)
                                        : '0.00'}%
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="general" activeValue={activeTab} onValueChange={setActiveTab}>
                            Geral
                        </TabsTrigger>
                        {!isNew && (
                            <TabsTrigger value="bom" activeValue={activeTab} onValueChange={setActiveTab}>
                                BOM
                            </TabsTrigger>
                        )}
                        {!isNew && (
                            <TabsTrigger value="reports" activeValue={activeTab} onValueChange={setActiveTab}>
                                Registro de Produção
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {/* Tab: Geral */}
                    <TabsContent value="general" activeValue={activeTab} className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Informações da Ordem de Produção</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-6" autoComplete="off">
                                    {/* Seção: Informações Gerais */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-md font-semibold text-gray-900">
                                                Informações Gerais
                                            </h3>
                                        </div>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Produto *
                                                </label>
                                                <Autocomplete
                                                    options={products}
                                                    getOptionLabel={(option) => `${option.code} - ${option.description}`}
                                                    getOptionValue={(option) => option.id}
                                                    value={formData.product_id ? parseInt(formData.product_id) : null}
                                                    onChange={handleProductSelect}
                                                    required
                                                    disabled={readOnly || (!isNew && !!order)}
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Quantidade Planejada *
                                                    </label>
                                                    <Input
                                                        type="number"
                                                        step="0.001"
                                                        name="planned_quantity"
                                                        value={formData.planned_quantity}
                                                        onChange={handleFormChange}
                                                        required
                                                        min="1"
                                                        disabled={readOnly}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Data *
                                                    </label>
                                                    <MaskedInput
                                                        mask="date"
                                                        name="date"
                                                        value={formData.date}
                                                        onChange={handleFormChange}
                                                        required
                                                        disabled={readOnly}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Seção: Depósitos */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                                </svg>
                                            </div>
                                            <h3 className="text-md font-semibold text-gray-900">
                                                Depósitos
                                            </h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Depósito de destino *
                                                </label>
                                                <select
                                                    name="target_warehouse_id"
                                                    value={formData.target_warehouse_id}
                                                    onChange={handleFormChange}
                                                    className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                                                    required
                                                    disabled={readOnly}
                                                >
                                                    <option value="">Selecione...</option>
                                                    {warehouses.map((warehouse) => (
                                                        <option key={warehouse.id} value={warehouse.id}>
                                                            {warehouse.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Seção: Observações */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-md font-semibold text-gray-900">
                                                Observações
                                            </h3>
                                        </div>
                                        <div>
                                            <textarea
                                                name="notes"
                                                value={formData.notes}
                                                onChange={handleFormChange}
                                                rows={4}
                                                disabled={readOnly}
                                                className="flex w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab: BOM */}
                    {!isNew && (
                        <TabsContent value="bom" activeValue={activeTab} className="mt-6">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Estrutura de Materiais (BOM)</CardTitle>
                                        {canEdit && (
                                            <Button type="button" onClick={openBomDrawer}>
                                                Alterar BOM
                                            </Button>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <BOMEditor
                                        productId={order?.product_id}
                                        bomData={bomData}
                                        supplies={bomSuppliesCatalog}
                                        resources={bomResourcesCatalog}
                                        readOnly
                                        hideSaveButton
                                    />
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}

                    {/* Tab: Registro de Produção */}
                    {!isNew && (
                        <TabsContent value="reports" activeValue={activeTab} className="mt-6">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Registros de Produção</CardTitle>
                                        {order && order.status !== 'finished' && order.status !== 'cancelled' && (
                                            <Button onClick={() => setReportDrawerOpen(true)}>
                                                Registrar Produção
                                            </Button>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <TableComponent
                                        columns={reportColumns}
                                        data={reports}
                                        emptyMessage="Nenhum registro de produção"
                                    />
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                </Tabs>
            </div>

            {/* Report Drawer */}
            <Drawer
                open={reportDrawerOpen}
                onClose={() => {
                    setReportDrawerOpen(false);
                    setReportFormKey((k) => k + 1);
                }}
                title="Registrar Produção"
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setReportDrawerOpen(false);
                                setReportFormKey((k) => k + 1);
                            }}
                            disabled={reportLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            form="production-report-form"
                            disabled={reportLoading}
                        >
                            {reportLoading ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                <ProductionReportForm
                    key={reportFormKey}
                    movementTypes={movementTypes}
                    defaultMovementTypeId={defaultProdMovementTypeId}
                    productLabel={order?.product ? `${order.product.code} - ${order.product.description}` : ''}
                    warehouseLabel={order?.target_warehouse?.name || ''}
                    onSubmit={handleReportSubmit}
                    loading={reportLoading}
                />
            </Drawer>

            {/* Cancel Dialog */}
            <ConfirmDialog
                open={cancelDialog.open}
                title="Cancelar Ordem de Produção"
                message="Tem certeza que deseja cancelar esta ordem de produção? Esta ação não pode ser desfeita."
                onConfirm={handleCancel}
                onCancel={() => setCancelDialog({ open: false })}
            />

            {/* Finish Dialog */}
            <ConfirmDialog
                open={finishDialog.open}
                title="Finalizar Ordem de Produção"
                message="Tem certeza que deseja finalizar esta ordem de produção? Não será possível fazer mais alterações."
                onConfirm={handleFinish}
                onCancel={() => setFinishDialog({ open: false })}
            />

            {/* BOM Drawer */}
            <Drawer
                open={bomDrawerOpen}
                onClose={closeBomDrawer}
                title="Alterar BOM"
                size="xl"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={closeBomDrawer} disabled={bomSaving}>
                            Cancelar
                        </Button>
                        <Button
                            type="button"
                            onClick={() => bomEditorRef.current?.handleSave()}
                            disabled={bomSaving}
                        >
                            {bomSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                {bomDraft && (
                    <BOMEditor
                        key={bomDrawerKey}
                        ref={bomEditorRef}
                        productId={order?.product_id}
                        bomData={bomDraft}
                        supplies={bomSuppliesCatalog}
                        resources={bomResourcesCatalog}
                        onChange={setBomDraft}
                        onSave={handleBomSave}
                        hideSaveButton
                    />
                )}
            </Drawer>
        </AppLayout>
    );
}

export default ProductionOrderDetail;

